import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { API_CONFIG } from "../../config/api";

export enum AgreementStatus {
    DRAFT = "draft",
    PENDING_REVIEW = "pending_review",
    PENDING_CHANGES = "pending_changes",
    ACCEPTED = "accepted",
    REJECTED = "rejected",
}

export interface TenancyAgreement {
    id: string;
    organization_id?: string;
    lease_id: string;
    lease_code?: string;
    property_id: string;
    property_name?: string;
    tenant_id: string;
    tenant_name?: string;
    document_url: string;
    version: number;
    status: AgreementStatus;
    tenant_initials?: string;
    tenant_signed_at?: string;
    tenant_notes?: string;
    uploaded_by_id?: string;
    uploaded_by_name?: string;
    uploaded_at: string;
    admin_notes?: string;
    created_at: string;
    updated_at: string;
}

export interface TenancyAgreementCreateRequest {
    lease_id: string;
    document_file: string; // Base64 encoded
    admin_notes?: string;
}

export interface TenancyAgreementUpdateRequest {
    status?: AgreementStatus;
    tenant_initials?: string;
    tenant_notes?: string;
    admin_notes?: string;
    document_file?: string; // Base64 encoded for new version
}

export interface TenancyAgreementListResponse {
    count: number;
    next?: string;
    previous?: string;
    results: TenancyAgreement[];
}

export const tenancyAgreementsApi = createApi({
    reducerPath: "tenancyAgreementsApi",
    baseQuery: fetchBaseQuery({
        baseUrl: API_CONFIG.baseUrl,
        prepareHeaders: (headers, { getState }) => {
            const token = (getState() as any).auth.accessToken;
            if (token) {
                headers.set("authorization", `Bearer ${token}`);
            }
            return headers;
        },
    }),
    tagTypes: ["TenancyAgreements"],
    endpoints: (builder) => ({
        /**
         * Create/upload a tenancy agreement (admin only)
         */
        createTenancyAgreement: builder.mutation<
            TenancyAgreement,
            TenancyAgreementCreateRequest
        >({
            query: (data) => ({
                url: "/tenancy-agreements/",
                method: "POST",
                body: data,
            }),
            invalidatesTags: ["TenancyAgreements"],
        }),

        /**
         * List tenancy agreements with filters
         */
        listTenancyAgreements: builder.query<
            TenancyAgreementListResponse,
            {
                status?: AgreementStatus;
                lease_id?: string;
                property_id?: string;
                tenant_id?: string;
                ordering?: string;
                page?: number;
                page_size?: number;
            }
        >({
            query: (params) => {
                const queryParams = new URLSearchParams();
                if (params.status) queryParams.append("status", params.status);
                if (params.lease_id) queryParams.append("lease_id", params.lease_id);
                if (params.property_id) queryParams.append("property_id", params.property_id);
                if (params.tenant_id) queryParams.append("tenant_id", params.tenant_id);
                if (params.ordering) queryParams.append("ordering", params.ordering);
                if (params.page) queryParams.append("page", params.page.toString());
                if (params.page_size) queryParams.append("page_size", params.page_size.toString());

                const queryString = queryParams.toString();
                return {
                    url: queryString ? `/tenancy-agreements/?${queryString}` : "/tenancy-agreements/",
                    method: "GET",
                };
            },
            providesTags: ["TenancyAgreements"],
        }),

        /**
         * Get a single tenancy agreement by ID
         */
        getTenancyAgreement: builder.query<TenancyAgreement, string>({
            query: (id) => ({
                url: `/tenancy-agreements/${id}`,
                method: "GET",
            }),
            providesTags: (result, error, id) => [{ type: "TenancyAgreements", id }],
        }),

        /**
         * Update a tenancy agreement
         */
        updateTenancyAgreement: builder.mutation<
            TenancyAgreement,
            { id: string; data: TenancyAgreementUpdateRequest }
        >({
            query: ({ id, data }) => ({
                url: `/tenancy-agreements/${id}`,
                method: "PATCH",
                body: data,
            }),
            invalidatesTags: (result, error, { id }) => [
                "TenancyAgreements",
                { type: "TenancyAgreements", id },
            ],
        }),
    }),
});

export const {
    useCreateTenancyAgreementMutation,
    useListTenancyAgreementsQuery,
    useGetTenancyAgreementQuery,
    useUpdateTenancyAgreementMutation,
} = tenancyAgreementsApi;
